// DashboardConfigurator.tsx
import React, {useEffect, useState} from 'react';
import {Select, Button, Card, message} from 'antd';
import {DragDropContext, Droppable, Draggable, DropResult} from '@hello-pangea/dnd';
import {useDispatch, useSelector} from 'react-redux';
import {
    useLazyGetApiDashboardLayoutQuery,
    useLazyGetApiDashboardWidgetsQuery,
    usePostApiDashboardLayoutMutation
} from '../../api/services/Dashboard';
import {setError, setLoading} from '../../features/app/appSlice';
import {RootState} from '../../app/store';
import {AuthorizationIds} from '../../hooks/Authorization/AuthorizationIds';

const {Option} = Select;

// max widget sayısı dinamik olarak layout boyutuna göre hesaplanacak


interface Widget {
    id: string;
    name: string;
    componentKey: string;
    allowedRoles: string[];
}

interface WidgetPlacement {
    row: number;
    col: number;
    widgetId: string;
}

const layoutOptions = [
    {key: '1x1', rows: 1, cols: 1},
    {key: '1x2', rows: 1, cols: 2},
    {key: '1x3', rows: 1, cols: 3},
    {key: '2x1', rows: 2, cols: 1},
    {key: '2x2', rows: 2, cols: 2},
    {key: '2x3', rows: 2, cols: 3},
    {key: '3x1', rows: 3, cols: 1},
    {key: '3x2', rows: 3, cols: 2},
    {key: '3x3', rows: 3, cols: 3}
];

const DashboardConfigurator: React.FC = () => {
    const dispatch = useDispatch();
    const user = useSelector((state: RootState) => state.auth?.user);
    const [roles] = useState(AuthorizationIds.List);
    const [selectedRole, setSelectedRole] = useState<string>(AuthorizationIds.List[0].id);
    const [selectedLayout, setSelectedLayout] = useState<string>('3x3');
    const [allWidgets, setAllWidgets] = useState<Widget[]>([]);
    const [activeWidgets, setActiveWidgets] = useState<WidgetPlacement[]>([]);
    const [fetchWidgets] = useLazyGetApiDashboardWidgetsQuery();
    const [fetchLayout] = useLazyGetApiDashboardLayoutQuery();
    const [saveLayout] = usePostApiDashboardLayoutMutation();

    const layoutConfig = layoutOptions.find(l => l.key === selectedLayout)!;
    const getWidgetAt = (row: number, col: number) =>
        activeWidgets.find(w => w.row === row && w.col === col);

    const handleRemoveWidgetAt = (row: number, col: number) => {
        setActiveWidgets(prev => prev.filter(w => !(w.row === row && w.col === col)));
    };

    const getAvailableWidgets = () =>
        allWidgets.filter(w =>
            w.allowedRoles.includes(selectedRole) &&
            !activeWidgets.some(p => p.widgetId === w.id)
        );

    const getLayout = async () => {
        if (!selectedRole || !user?.companyId) return;
        try {
            dispatch(setLoading(true));
            const layout = await fetchLayout({
                companyId: user.companyId,
                role: selectedRole
            }).unwrap();

            if (!layout) {
                setSelectedLayout('1x1');
                setActiveWidgets([]);
                return;
            }

            // önce layout'u setle
            // @ts-ignore
            if (layout.layoutKey) {
                // @ts-ignore
                setSelectedLayout(layout.layoutKey);
            } else {
                setSelectedLayout('1x1');
            }

            // @ts-ignore
            setActiveWidgets(layout.grid ?? []);
        } catch (err: any) {
            if (err.status !== 'PARSING_ERROR') {
                dispatch(setError('Layoutlar alınırken hata oluştu'));
            }
            // Hata durumunda da sıfırla
            setSelectedLayout('1x1');
            setActiveWidgets([]);
        } finally {
            dispatch(setLoading(false));
        }
    };


    const getWidgets = async () => {
        try {
            dispatch(setLoading(true));
            const widgets = await fetchWidgets().unwrap();
            setAllWidgets(widgets as any);
        } catch (err: any) {
            if (err.status !== 'PARSING_ERROR') {
                dispatch(setError('Widgetlar alınırken hata oluştu'));
            }
        } finally {
            dispatch(setLoading(false));
        }
    };

    const handleSaveLayout = async () => {
        try {
            dispatch(setLoading(true));
            await saveLayout({
                dashboardLayout: {
                    companyId: user?.companyId ?? '',
                    role: selectedRole,
                    grid: activeWidgets,
                    layoutKey: selectedLayout
                }
            }).unwrap();
            message.success('Dashboard kaydedildi');
        } catch (err: any) {
            if (err.status !== 'PARSING_ERROR') {
                dispatch(setError('Layout kaydedilirken hata oluştu'));
            }
        } finally {
            dispatch(setLoading(false));
        }
    };

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const [_, toRowStr, toColStr] = result.destination.droppableId.split('-');
        const toRow = parseInt(toRowStr);
        const toCol = parseInt(toColStr);

        if (result.source.droppableId === 'widget-pool') {
            if (getWidgetAt(toRow, toCol)) return;
            const maxWidgets = layoutConfig.rows * layoutConfig.cols;
            if (activeWidgets.length >= maxWidgets) {
                message.error(`En fazla ${maxWidgets} widget seçebilirsiniz`);
                return;
            }
            setActiveWidgets(prev => [...prev, {row: toRow, col: toCol, widgetId: result.draggableId}]);
        } else {
            const [__, fromRowStr, fromColStr] = result.source.droppableId.split('-');
            const fromRow = parseInt(fromRowStr);
            const fromCol = parseInt(fromColStr);

            const fromIndex = activeWidgets.findIndex(w => w.row === fromRow && w.col === fromCol);
            const toIndex = activeWidgets.findIndex(w => w.row === toRow && w.col === toCol);

            setActiveWidgets(prev => {
                const updated = [...prev];
                if (toIndex === -1) {
                    updated[fromIndex] = {...updated[fromIndex], row: toRow, col: toCol};
                } else {
                    const temp = {...updated[fromIndex]};
                    updated[fromIndex] = {...updated[toIndex], row: fromRow, col: fromCol};
                    updated[toIndex] = {...temp, row: toRow, col: toCol};
                }
                return updated;
            });
        }
    };

    useEffect(() => {
        void getWidgets();
    }, []);

    useEffect(() => {
        void getLayout();
    }, [selectedRole]);


    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <span className="font-medium">Seçilen Rol:</span>
                        <Select style={{width: 200}} onChange={setSelectedRole} value={selectedRole}>
                            {roles.map(role => (
                                <Option key={role.id} value={role.id}>{role.name}</Option>
                            ))}
                        </Select>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="font-medium">Yerleşim:</span>
                        <Select style={{width: 120}}
                                value={selectedLayout} onChange={(value) => {
                            setSelectedLayout(value);
                            setActiveWidgets([]); // seçilen layout değiştiğinde temizle
                        }}>
                            {layoutOptions.map(opt => (
                                <Option key={opt.key} value={opt.key}>{opt.key}</Option>
                            ))}
                        </Select>
                    </div>
                </div>
                <Button type="primary" onClick={handleSaveLayout}>Kaydet</Button>
            </div>

            <DragDropContext onDragEnd={onDragEnd}>
                <Card title="Dashboard Görünümü">
                    <div
                        className="gap-4"
                        style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${layoutConfig.cols}, minmax(0, 1fr))`
                        }}
                    >
                        {[...Array(layoutConfig.rows)].map((_, row) =>
                            [...Array(layoutConfig.cols)].map((_, col) => {
                                const widget = getWidgetAt(row, col);
                                const meta = widget ? allWidgets.find(w => w.id === widget.widgetId) : null;

                                return (
                                    <Droppable droppableId={`grid-${row}-${col}`} key={`${row}-${col}`}>
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                                className={`relative border rounded min-h-[100px] p-2 flex items-center justify-center transition ${snapshot.isDraggingOver ? 'bg-blue-50' : 'bg-gray-50'}`}
                                            >
                                                {widget && meta ? (
                                                    <Draggable draggableId={widget.widgetId}
                                                               index={row * layoutConfig.cols + col}>
                                                        {(dragProvided) => (
                                                            <div
                                                                ref={dragProvided.innerRef}
                                                                {...dragProvided.draggableProps}
                                                                {...dragProvided.dragHandleProps}
                                                                className="relative bg-white border rounded p-3 shadow-sm hover:shadow-md w-full text-center"
                                                            >
                                                                <div className="absolute top-1 right-1">
                                                                    <Button
                                                                        size="small"
                                                                        type="text"
                                                                        danger
                                                                        onClick={() => handleRemoveWidgetAt(row, col)}
                                                                    >
                                                                        ×
                                                                    </Button>
                                                                </div>
                                                                <div className="font-semibold text-sm">{meta.name}</div>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ) : (
                                                    <span className="text-sm text-gray-400">(boş)</span>
                                                )}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                );
                            })
                        )}
                    </div>
                </Card>

                <Card title="Kullanılabilir Widgetlar" className="mt-6">
                    <Droppable droppableId="widget-pool" direction="horizontal" isDropDisabled>
                        {(provided) => (
                            <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                className="flex flex-wrap gap-2"
                            >
                                {getAvailableWidgets().length === 0 ? (
                                    <div className="text-gray-400 text-sm italic">Bütün widgetlar kullanıldı</div>
                                ) : (
                                    getAvailableWidgets().map((widget, index) => (
                                        <Draggable draggableId={widget.id} index={index} key={widget.id}>
                                            {(provided) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    className="border rounded px-4 py-2 bg-white shadow cursor-move hover:shadow-md transition"
                                                >
                                                    {widget.name}
                                                </div>
                                            )}
                                        </Draggable>
                                    ))
                                )}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </Card>
            </DragDropContext>
        </div>
    );
};

export default DashboardConfigurator;
